import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface LogsFiltersProps {
  selected: string[];
  onSelect: (selected: string[]) => void;
  title?: string;
}

export function LogsFilters({ selected, onSelect, title }: LogsFiltersProps) {
  const [open, setOpen] = useState(false);
  const { t, dir } = useLanguage();
  const isRTL = dir === 'rtl';

  const options = [
    { value: 'personal', label: t('filters.personal') },
    { value: 'default', label: t('filters.default') },
  ];

  const handleSelect = (value: string, checked: boolean) => {
    if (checked) {
      onSelect([...selected, value]);
    } else {
      onSelect(selected.filter(item => item !== value));
    }
  };

  const selectedCount = selected.length;
  const displayText = selectedCount === 0 
    ? title || t('filters.typeTitle')
    : selectedCount === 1
    ? options.find(opt => opt.value === selected[0])?.label
    : `${selectedCount} ${t('filters.selected')}`;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className={`h-9 w-full ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          {displayText}
          <ChevronDown className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align={isRTL ? "start" : "end"} 
        className={`w-48 ${isRTL ? 'text-right' : 'text-left'}`}
      >
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selected.includes(option.value)}
            onCheckedChange={(checked) => handleSelect(option.value, checked)}
            className={isRTL ? 'text-right' : 'text-left'}
            dir={dir}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
